import { useState, useEffect } from 'react';
import { Routes, Route, Link, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginPage } from './pages/Login';
import { TranscriptsPage } from './pages/Transcripts';
import { AdminPage } from './pages/Admin';
import { SoakTestPage } from './pages/SoakTest';
import { ToastContainer } from './components/Toast';
import { OfflineIndicator } from './components/StatusIndicators';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppContent() {
  const { isAuthenticated, logout, accountId } = useAuth();
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <div className="app">
      <OfflineIndicator isOffline={isOffline} />
      <ToastContainer />
      {isAuthenticated && (
        <nav className="navbar">
          <div className="nav-brand">
            <Link to="/">FieldVoice</Link>
          </div>
          <div className="nav-links">
            <Link to="/">Transcripts</Link>
            <Link to="/soak-test">Soak Tests</Link>
            <Link to="/admin">Admin</Link>
          </div>
          <div className="nav-user">
            <span className="nav-account">{accountId?.slice(0, 8)}...</span>
            <button className="nav-logout" onClick={logout}>
              Logout
            </button>
          </div>
        </nav>
      )}

      <main className={isAuthenticated ? 'main-content' : 'main-content-full'}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <TranscriptsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/soak-test"
            element={
              <ProtectedRoute>
                <SoakTestPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>

      {isAuthenticated && (
        <footer className="footer">
          <p>FieldVoice Dashboard v1.0.0</p>
        </footer>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
